import { style, globalStyle, keyframes } from '@vanilla-extract/css';
import { vars } from '../../theme/contract.css';
import { reducedMotion, mobile } from '../../theme/media';
import {
  glassRimLight,
  glassGlowLine,
  glassGlowBleed,
  textOnGlassStrong,
} from '../../styles/glass.css';

// --- Keyframes ---

const pulseGlow = keyframes({
  '0%, 100%': { boxShadow: vars.stateColor.success.ringShadow },
  '50%': { boxShadow: vars.stateColor.success.hoverShadow },
});

const breathe = keyframes({
  '0%, 100%': { opacity: '1' },
  '50%': { opacity: '0.6' },
});

// --- Container ---

export const container = style({
  ...textOnGlassStrong,
  flexShrink: 0,
  overflow: 'hidden',
  position: 'relative',
  contain: 'layout paint',
  isolation: 'isolate',
  background: `radial-gradient(ellipse at 50% 40%, ${vars.opacityScale.whiteOverlay.light} 0%, transparent 40%, ${vars.opacityScale.blackOverlay.fade} 100%), linear-gradient(135deg, ${vars.surface.liquidGlass.innerSurface.background}, ${vars.surface.highlight.faint})`,
  backdropFilter: vars.surface.liquidGlass.backdropFilter.medium,
  border: `2px solid ${vars.surface.border.accent}`,
  borderRadius: vars.spacing.borderRadius.lg,
  padding: '16px',
  boxShadow: `inset 0 0 20px ${vars.opacityScale.blackOverlay.medium}, inset 0 1px 0 ${vars.opacityScale.whiteOverlay.veil}, inset 0 -2px 6px ${vars.opacityScale.blackOverlay.strong}, 0 0 6px ${vars.bright.primary.glow}`,
  transition: `box-shadow ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}, border-color ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}`,

  '@media': {
    [reducedMotion]: { transition: 'none' },
    [mobile]: { padding: '12px' },
  },
});

globalStyle(`${container}::after`, glassRimLight);

export const glowEffect = style({
  ...glassGlowLine,
  zIndex: vars.elevation.decorative,
  opacity: 'var(--phase-glow-opacity, 0)',
  transition: `opacity ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}`,
  '@media': { [reducedMotion]: { transition: 'none' } },
});

export const glowBleedEffect = style({
  ...glassGlowBleed,
  zIndex: vars.elevation.base,
  opacity: 'var(--phase-glow-opacity, 0)',
  transition: `opacity ${vars.animation.duration.normal} ${vars.animation.easing.easeOut}`,
  '@media': { [reducedMotion]: { transition: 'none' } },
});

// --- Status row (inline: dot · label · clock-in · elapsed) ---

export const statusRow = style({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'wrap',
});

export const statusDot = style({
  display: 'inline-block',
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  flexShrink: 0,
});

export const statusDotWorking = style({
  background: vars.bright.status.success,
  boxShadow: `0 0 6px ${vars.bright.status.success}`,
  animationName: breathe,
  animationDuration: `var(--phase-breathe-duration, ${vars.animation.duration.xslow})`,
  animationTimingFunction: vars.animation.easing.easeInOut,
  animationIterationCount: 'infinite',
  '@media': {
    [reducedMotion]: { animation: 'none', opacity: '0.8' },
  },
});

export const statusDotPaused = style({
  background: vars.bright.status.warning,
  boxShadow: `0 0 6px ${vars.bright.status.warning}`,
});

export const statusDotDone = style({
  background: vars.typography.textColor.secondary,
});

export const statusLabel = style({
  fontSize: vars.typography.fontSize.sm,
  fontWeight: vars.typography.fontWeight.bold,
  color: vars.typography.textColor.primary,
});

export const clockInTime = style({
  fontSize: vars.typography.fontSize.sm,
  color: vars.typography.textColor.secondary,
  fontFamily: vars.typography.fontFamily.mono,
});

export const arrow = style({
  fontSize: vars.typography.fontSize.sm,
  color: vars.typography.textColor.tertiary,
});

export const clockOutTime = clockInTime;

export const elapsed = style({
  fontSize: vars.typography.fontSize.base,
  fontWeight: vars.typography.fontWeight.bold,
  color: vars.typography.textColor.primary,
  fontFamily: vars.typography.fontFamily.mono,
  marginLeft: 'auto',
});

// --- Action buttons row ---

export const actions = style({
  display: 'flex',
  gap: vars.spacing.gap.md,
  marginTop: vars.spacing.gap.md,

  '@media': {
    [mobile]: {
      gap: vars.spacing.gap.sm,
    },
  },
});

// --- Base button ---

export const actionButton = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: vars.spacing.gap.md,
  flex: 1,
  padding: `${vars.spacing.padding.md} ${vars.spacing.padding.xl}`,
  borderRadius: vars.spacing.borderRadius.lg,
  border: '2px solid transparent',
  fontFamily: vars.typography.fontFamily.primary,
  fontSize: vars.typography.fontSize.base,
  fontWeight: vars.typography.fontWeight.bold,
  cursor: 'pointer',
  outline: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: `background ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, border-color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, box-shadow ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, transform ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
  letterSpacing: vars.typography.letterSpacing.normal,

  '@media': {
    [reducedMotion]: { transition: 'none', animation: 'none' },
    [mobile]: {
      padding: `${vars.spacing.padding.md} ${vars.spacing.padding.lg}`,
      fontSize: vars.typography.fontSize.sm,
    },
  },

  selectors: {
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      filter: 'grayscale(0.3)',
    },
  },
});

export const iconWrapper = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '22px',
  height: '22px',
  flexShrink: 0,
});

// --- Clock-in (출근) ---

export const clockInButton = style({
  background: vars.stateColor.success.light,
  borderColor: vars.stateColor.success.border,
  color: vars.bright.status.success,
  boxShadow: vars.stateColor.success.glow,
  fontSize: vars.typography.fontSize.lg,
  padding: `${vars.spacing.padding.lg} ${vars.spacing.padding.xl}`,

  selectors: {
    '&:hover:not(:disabled)': {
      background: vars.stateColor.success.strong,
      borderColor: vars.stateColor.success.hoverBorder,
      boxShadow: vars.stateColor.success.hoverGlow,
      transform: 'translateY(-1px)',
    },
    '&:active:not(:disabled)': {
      transform: 'translateY(0)',
    },
  },
});

export const clockInPulse = style({
  animation: `${pulseGlow} ${vars.animation.duration.xslow} ease-in-out infinite`,
  '@media': { [reducedMotion]: { animation: 'none' } },
});

// --- Clock-out (퇴근) · Break (휴식) ---
// Button atom(soft × danger · soft × warning · md)이다 — DESIGN.md §Atom Standards 1-2 가
// 이 둘을 `soft` 의 실측 수요로 든다. 출근·재개는 success 톤이 없어 page-local 로 남는다.
// 폭은 `fullWidth`(width 100%)가 아니라 flex: 1 로 나눈다 — 옆의 page-local 재개(flex: 1)가 눌려 세로로 접힌다.
export const actionGrow = style({ flex: 1 });

// --- Resume (재개) ---

export const resumeButton = style({
  background: vars.stateColor.success.light,
  borderColor: vars.stateColor.success.borderSubtle,
  color: vars.bright.status.success,
  boxShadow: vars.stateColor.success.glow,

  selectors: {
    '&:hover:not(:disabled)': {
      background: vars.stateColor.success.strong,
      borderColor: vars.stateColor.success.hoverBorder,
      boxShadow: vars.stateColor.success.hoverGlow,
      transform: 'translateY(-1px)',
    },
    '&:active:not(:disabled)': {
      transform: 'translateY(0)',
    },
  },
});

// --- Auto-closed (자동 마감) — cron force-close, 일반 퇴근과 구분 ---

export const statusDotAuto = style({
  background: vars.bright.status.warning,
  boxShadow: `0 0 6px ${vars.bright.status.warning}`,
});

export const autoClosedLabel = style({
  fontSize: vars.typography.fontSize.sm,
  fontWeight: vars.typography.fontWeight.bold,
  color: vars.bright.status.warning,
});

export const autoClosedHint = style({
  margin: 0,
  marginTop: vars.spacing.gap.sm,
  fontSize: vars.typography.fontSize.sm,
  color: vars.typography.textColor.secondary,
});

// 자동 마감 후 재출근은 1차 CTA가 아님 — 녹색 강조 대신 중립 톤으로 유도 억제
export const reclockMutedButton = style({
  background: vars.surface.highlight.faint,
  borderColor: vars.surface.border.secondary,
  color: vars.typography.textColor.secondary,

  selectors: {
    '&:hover:not(:disabled)': {
      background: vars.surface.highlight.light,
      borderColor: vars.surface.border.primary,
      transform: 'translateY(-1px)',
    },
    '&:active:not(:disabled)': {
      transform: 'translateY(0)',
    },
  },
});

// --- Correction (정정) ---

export const correctionButton = style({
  background: vars.surface.highlight.faint,
  borderColor: vars.surface.border.secondary,
  color: vars.typography.textColor.secondary,
  flex: 'none',

  selectors: {
    '&:hover:not(:disabled)': {
      background: vars.surface.highlight.light,
      borderColor: vars.surface.border.primary,
      transform: 'translateY(-1px)',
    },
    '&:active:not(:disabled)': {
      transform: 'translateY(0)',
    },
  },
});
